import { z } from 'zod';

// Base utility schemas
const CoordinatesSchema = z.object({
  lat: z.number().nullable(),
  lng: z.number().nullable(),
});

const PriceSchema = z.object({
  amount: z.number(),
  currency: z.string().length(3), // ISO 4217 currency code
});

const DateRangeSchema = z.object({
  start: z.date(),
  end: z.date(),
});

const LocationSchema = z.object({
  address: z.string().optional().default('').nullable(),
  city: z.string(),
  country: z.string(),
  region: z.string(),
  coordinates: CoordinatesSchema.optional().nullable(),
  placeId: z.string().optional(),
});

const AccommodationTypeSchema = z.enum([
  'hotel',
  'resort',
  'apartment',
  'hostel',
  'bnb',
  'any',
]);

const DietaryRestrictionSchema = z.enum([
  'vegetarian',
  'vegan',
  'gluten_free',
  'dairy_free',
  'nut_free',
  'halal',
  'kosher',
  'keto',
  'paleo',
]);

const FlightClassSchema = z.enum([
  'economy',
  'premium_economy',
  'business',
  'first_class',
]);

// ===== SERVICE TIER =====

const ServiceTierLevelSchema = z.enum(['self_service']);

export {
  CoordinatesSchema,
  PriceSchema,
  DateRangeSchema,
  LocationSchema,
  ServiceTierLevelSchema,
  AccommodationTypeSchema,
  DietaryRestrictionSchema,
  FlightClassSchema,
};

export type ServiceTier = {
  id?: string;
  value: ServiceTierLevel;
  name: string;
  price: string;
  priceCents: number;
  description: string;
  features: string[];
  popular: boolean;
  iconName: string; // Store icon name as string instead of React component
  createdAt?: Date;
  updatedAt?: Date;
};

// Service tier options
export const SERVICE_TIERS: ServiceTier[] = [
  {
    value: 'self_service' as ServiceTierLevel,
    name: 'Self-Service',
    price: '$8',
    priceCents: 800,
    description: 'Self planned itinerary with recommendations',
    features: [
      'AI-assisted itinerary options',
      'Curated restaurants & activities',
      'Drag-and-drop customization',
      'Beautiful shareable trip itinerary',
      'Mobile-friendly planning',
    ],
    popular: true,
    iconName: 'Sparkles',
  },
];

export type ServiceTierLevel = z.infer<typeof ServiceTierLevelSchema>;
export type PaymentType = 'single_purchase' | 'monthly' | 'annual';

// Inferred TypeScript types
export type Coordinates = z.infer<typeof CoordinatesSchema>;
export type Price = z.infer<typeof PriceSchema>;
export type DateRange = z.infer<typeof DateRangeSchema>;
export type Location = z.infer<typeof LocationSchema>;
export type AccommodationType = z.infer<typeof AccommodationTypeSchema>;
export type DietaryRestriction = z.infer<typeof DietaryRestrictionSchema>;
export type FlightClass = z.infer<typeof FlightClassSchema>;

// ===== STANDARDIZED ACTIVITY TAXONOMY =====

// ===== TRAVEL ACTIVITY ONBOARDING DATA STRUCTURE =====

export type GroupedInterest = {
  id: string;
  name: string;
  icon: string;
  activities: Interest[];
};
export const InterestSchema = z.object({
  id: z.string(),
  name: z.string(),
  keywords: z.array(z.string()),
  googlePlaceType: z.string(),
  foursquareCategory: z.string(),
  tripAdvisorType: z.string(),
  icon: z.string(), // Lucide icon name
});
export type Interest = z.infer<typeof InterestSchema>;

export const GroupedInterests: GroupedInterest[] = [
  {
    id: 'culture-history',
    name: 'Culture & History',
    icon: '🎨',
    activities: [
      {
        id: 'museum',
        name: 'Museums',
        keywords: ['museum'],
        googlePlaceType: 'museum',
        foursquareCategory: '4bf58dd8d48988d181941735',
        tripAdvisorType: 'museums',
        icon: 'Landmark',
      },
      {
        id: 'art_gallery',
        name: 'Art Galleries',
        keywords: ['art', 'gallery', 'exhibition', 'cultural center'],
        googlePlaceType: 'art_gallery',
        foursquareCategory: '4bf58dd8d48988d1e2931735',
        tripAdvisorType: 'art_galleries',
        icon: 'Palette',
      },
      {
        id: 'historical-sites',
        name: 'Historical Sites',
        keywords: [
          'historical',
          'monument',
          'heritage',
          'archaeological',
          'ancient',
        ],
        googlePlaceType: 'historical sites & landmarks',
        foursquareCategory: '4bf58dd8d48988d12d951735',
        tripAdvisorType: 'historic',
        icon: 'Castle',
      },
    ],
  },
  {
    id: 'entertainment',
    name: 'Entertainment & Shows',
    icon: '🎭',
    activities: [
      {
        id: 'performing-arts',
        name: 'Performing Arts',
        keywords: [
          'theater',
          'theatre',
          'opera',
          'ballet',
          'concert',
          'performance',
        ],
        googlePlaceType: 'performing_arts_theater',
        foursquareCategory: '4bf58dd8d48988d17f941735',
        tripAdvisorType: 'theater',
        icon: 'Theater',
      },
      {
        id: 'live-music',
        name: 'Live Music',
        keywords: [
          'live music',
          'concert',
          'jazz',
          'rock',
          'classical',
          'venue',
        ],
        googlePlaceType: 'concert_hall',
        foursquareCategory: '4bf58dd8d48988d1e5931735',
        tripAdvisorType: 'concerts',
        icon: 'Music',
      },
      {
        id: 'comedy-shows',
        name: 'Comedy Shows',
        keywords: ['comedy', 'stand-up', 'improv', 'humor'],
        googlePlaceType: 'comedy_club',
        foursquareCategory: '4bf58dd8d48988d18e941735',
        tripAdvisorType: 'comedy',
        icon: 'Laugh',
      },
    ],
  },
  {
    id: 'outdoor-adventure',
    name: 'Outdoor Adventure',
    icon: '🏔️',
    activities: [
      {
        id: 'hiking-walking',
        name: 'Hiking & Walking Tours',
        keywords: ['hiking', 'walking', 'trail', 'trek', 'nature walk'],
        googlePlaceType: 'hiking_trail',
        foursquareCategory: '4bf58dd8d48988d159941735',
        tripAdvisorType: 'hiking',
        icon: 'Mountain',
      },
      {
        id: 'beach',
        name: 'Beaches',
        keywords: ['beach', 'water', 'sun', 'sand', 'ocean'],
        googlePlaceType: 'beach',
        foursquareCategory: '4bf58dd8d48988d1e1941735',
        tripAdvisorType: 'beaches',
        icon: 'Umbrella',
      },
      {
        id: 'parks-gardens',
        name: 'Parks & Gardens',
        keywords: ['park', 'garden', 'botanical', 'nature', 'green space'],
        googlePlaceType: 'park',
        foursquareCategory: '4bf58dd8d48988d163941735',
        tripAdvisorType: 'parks',
        icon: 'TreePine',
      },
    ],
  },
  {
    id: 'food-drink',
    name: 'Food & Drink Experiences',
    icon: '🍷',
    activities: [
      {
        id: 'food-tours',
        name: 'Food Tours & Tastings',
        keywords: [
          'food tour',
          'tasting',
          'culinary',
          'cooking class',
          'wine tasting',
        ],
        googlePlaceType: 'food_tour',
        foursquareCategory: '4bf58dd8d48988d1f0941735',
        tripAdvisorType: 'food-tours',
        icon: 'UtensilsCrossed',
      },
      {
        id: 'local-markets',
        name: 'Food Markets',
        keywords: [
          'market',
          'food hall',
          'local food',
          'street food',
          'farmers market',
        ],
        googlePlaceType: 'market',
        foursquareCategory: '50aa9e744b90af0d42d5de0d',
        tripAdvisorType: 'markets',
        icon: 'Store',
      },
    ],
  },
  {
    id: 'shopping',
    name: 'Shopping & Markets',
    icon: '🛍️',
    activities: [
      {
        id: 'gift-shop',
        name: 'Gift Shops',
        keywords: [
          'crafts',
          'souvenirs',
          'handmade',
          'artisan',
          'local products',
        ],
        googlePlaceType: 'gift_shop',
        foursquareCategory: '4bf58dd8d48988d1f6941735',
        tripAdvisorType: 'shopping',
        icon: 'Gift',
      },
      {
        id: 'shopping-mall',
        name: 'Shopping Malls',
        keywords: ['shopping', 'mall', 'retail', 'store', 'shopping center'],
        googlePlaceType: 'shopping_mall',
        foursquareCategory: '4bf58dd8d48988d1f5941735',
        tripAdvisorType: 'shopping',
        icon: 'ShoppingBag',
      },
    ],
  },
  {
    id: 'wellness-relaxation',
    name: 'Wellness & Relaxation',
    icon: '🧘',
    activities: [
      {
        id: 'spas-wellness',
        name: 'Spas & Massage',
        keywords: ['spa', 'wellness', 'massage', 'relaxation', 'health'],
        googlePlaceType: 'spa & massage',
        foursquareCategory: '4bf58dd8d48988d1ed941735',
        tripAdvisorType: 'spas',
        icon: 'Heart',
      },
    ],
  },
  {
    id: 'family-kids',
    name: 'Family & Kids',
    icon: '🎠',
    activities: [
      {
        id: 'amusement_park',
        name: 'Amusement Parks',
        keywords: [
          'amusement park',
          'adventure',
          'climbing',
          'bungee',
          'skydiving',
          'zip-line',
        ],
        googlePlaceType: 'amusement_park',
        foursquareCategory: '4bf58dd8d48988d175941735',
        tripAdvisorType: 'amusement-parks',
        icon: 'FerrisWheel',
      },
      {
        id: 'zoos-aquariums',
        name: 'Zoos',
        keywords: ['zoo', 'aquarium', 'wildlife', 'animals', 'marine life'],
        googlePlaceType: 'zoo & aquarium',
        foursquareCategory: '4bf58dd8d48988d17b941735',
        tripAdvisorType: 'zoos',
        icon: 'Fish',
      },
    ],
  },
  {
    id: 'sports-fitness',
    name: 'Sports & Fitness',
    icon: '🏆',
    activities: [
      {
        id: 'spectator-sports',
        name: 'Spectator Sports',
        keywords: ['sports', 'game', 'stadium', 'match', 'tournament'],
        googlePlaceType: 'stadium',
        foursquareCategory: '4bf58dd8d48988d184941735',
        tripAdvisorType: 'sports',
        icon: 'Trophy',
      },
    ],
  },
  {
    id: 'nightlife',
    name: 'Nightlife & Social',
    icon: '🌃',
    activities: [
      {
        id: 'bars-pubs',
        name: 'Bars & Pubs',
        keywords: ['bar', 'pub', 'cocktail', 'drinks', 'nightlife'],
        googlePlaceType: 'bar',
        foursquareCategory: '4bf58dd8d48988d116941735',
        tripAdvisorType: 'bars',
        icon: 'Wine',
      },
      {
        id: 'nightclubs-dancing',
        name: 'Nightclubs & Dancing',
        keywords: ['nightclub', 'dancing', 'club', 'DJ', 'party'],
        googlePlaceType: 'night_club',
        foursquareCategory: '4bf58dd8d48988d11f941735',
        tripAdvisorType: 'nightclubs',
        icon: 'Music2',
      },
    ],
  },
  {
    id: 'city-tours',
    name: 'City Tours',
    icon: '🚌',
    activities: [
      {
        id: 'guided-tours',
        name: 'Guided Tours',
        keywords: ['guided tour', 'tour guide', 'sightseeing', 'group tour'],
        googlePlaceType: 'tour_agency',
        foursquareCategory: '4bf58dd8d48988d1f0941735',
        tripAdvisorType: 'tours',
        icon: 'MapPin',
      },
    ],
  },
];

export const AllInterests: Interest[] = GroupedInterests.flatMap(group =>
  group.activities
    .map(activity => ({
      id: activity.id,
      name: activity.name,
      keywords: activity.keywords,
      googlePlaceType: activity.googlePlaceType,
      foursquareCategory: activity.foursquareCategory,
      tripAdvisorType: activity.tripAdvisorType,
      icon: activity.icon,
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
);

const interstNames = AllInterests.map(activity => activity.name);
export const InterestNamesSchema = z.enum(
  interstNames as [string, ...string[]]
);

// ===== TRIP MOTIVATION / REASON =====

export const TripMotivationSchema = z.enum([
  'family_vacation',
  'romantic_getaway',
  'surprise_partner',
  'solo_adventure',
  'friends_trip',
  'anniversary_celebration',
  'honeymoon',
  'milestone_birthday',
  'weekend_escape',
  'business_leisure',
]);

export type TripMotivation = z.infer<typeof TripMotivationSchema>;

export type TripMotivationOption = {
  id: TripMotivation;
  name: string;
  description: string;
  icon: string; // Lucide icon name
  suggestedInterests: string[]; // IDs from GroupedInterests
  defaultGroupType: 'solo' | 'couple' | 'family' | 'friends' | 'business';
};

export const TRIP_MOTIVATIONS: TripMotivationOption[] = [
  {
    id: 'family_vacation',
    name: 'Family Vacation',
    description: 'Create unforgettable memories with the whole family',
    icon: 'Users',
    suggestedInterests: ['family-kids', 'outdoor-adventure', 'entertainment'],
    defaultGroupType: 'family',
  },
  {
    id: 'romantic_getaway',
    name: 'Romantic Getaway',
    description: 'Escape together for romance and relaxation',
    icon: 'Heart',
    suggestedInterests: [
      'wellness-relaxation',
      'food-drink',
      'culture-history',
    ],
    defaultGroupType: 'couple',
  },
  {
    id: 'surprise_partner',
    name: 'Surprise My Partner',
    description: "Plan the perfect surprise they'll never forget",
    icon: 'Gift',
    suggestedInterests: ['culture-history', 'food-drink', 'entertainment'],
    defaultGroupType: 'couple',
  },
  {
    id: 'solo_adventure',
    name: 'Solo Adventure',
    description: 'Discover yourself through new experiences',
    icon: 'Compass',
    suggestedInterests: ['outdoor-adventure', 'culture-history', 'city-tours'],
    defaultGroupType: 'solo',
  },
  {
    id: 'friends_trip',
    name: 'Friends Trip',
    description: 'Adventure and fun with your favorite people',
    icon: 'PartyPopper',
    suggestedInterests: ['nightlife', 'outdoor-adventure', 'entertainment'],
    defaultGroupType: 'friends',
  },
  {
    id: 'anniversary_celebration',
    name: 'Anniversary',
    description: 'Celebrate your love story in style',
    icon: 'Sparkles',
    suggestedInterests: ['food-drink', 'wellness-relaxation', 'entertainment'],
    defaultGroupType: 'couple',
  },
  {
    id: 'honeymoon',
    name: 'Honeymoon',
    description: 'Begin your marriage with the trip of a lifetime',
    icon: 'HandHeart',
    suggestedInterests: [
      'wellness-relaxation',
      'food-drink',
      'outdoor-adventure',
    ],
    defaultGroupType: 'couple',
  },
  {
    id: 'milestone_birthday',
    name: 'Milestone Birthday',
    description: 'Make this birthday one to remember',
    icon: 'Cake',
    suggestedInterests: ['nightlife', 'entertainment', 'food-drink'],
    defaultGroupType: 'friends',
  },
  {
    id: 'weekend_escape',
    name: 'Weekend Escape',
    description: 'Quick getaway to recharge and explore',
    icon: 'Luggage',
    suggestedInterests: ['city-tours', 'food-drink', 'shopping'],
    defaultGroupType: 'couple',
  },
  {
    id: 'business_leisure',
    name: 'Business + Leisure',
    description: 'Mix work with pleasure on your trip',
    icon: 'Briefcase',
    suggestedInterests: ['city-tours', 'food-drink', 'culture-history'],
    defaultGroupType: 'business',
  },
];
